import { customMessages, customValidate, restrictInput } from '../modules/custom-validation.js';
import { errorPlacement, highlight, unhighlight, handleFormError } from '../modules/form-validation.js';
import { showRecaptchaError, waitForRecaptcha } from '../modules/functions.js';
import regex from '../modules/regex-rules.js';

const $ = window.jQuery;

if (!$ || !$.fn || !$.fn.validate) {
	throw new Error('jQuery sau jQuery Validate nu sunt disponibile.');
}

window.dataLayer = window.dataLayer || [];

class FormEv {
	constructor({ formContainer, formName, formType, formCategory, recaptcha }) {
		this.recaptcha = recaptcha;

		if (!formContainer || !(formContainer instanceof HTMLFormElement)) {
			console.error(formContainer);
			throw new Error('`formContainer` e undefined sau nu e instanta `HTMLFormElement`.');
		}

		if (!formContainer.dataset.fetchUrl) {
			console.error(formContainer);
			throw new Error(`#${formContainer.id}: data-fetch-url e undefined.`);
		}

		if (!formName) {
			throw new Error('`formName` e undefined.');
		}

		if (!formType) {
			throw new Error('`formType` e undefined.');
		}

		if (!formCategory) {
			throw new Error('`formCategory` e undefined.');
		}

		this.form = formContainer;

		this.responseContainers = {
			error: formContainer.querySelector('.error-response-container'),
			errorMsg: formContainer.querySelector('.error-response-container')?.querySelector('.nj-inline-message__body'),
			success: formContainer.querySelector('.success-response-container'),
			successMsg: formContainer.querySelector('.success-response-container')?.querySelector('.nj-inline-message__body'),
		};

		// Sursa mesaj afisat: 'validation' | 'recaptcha' | 'api' | null
		this.activeResponse = null;

		this.fieldIds = {
			name: formContainer.querySelector('[name="form-nume"]')?.id,
			email: formContainer.querySelector('[name="form-email"]')?.id,
			phone: formContainer.querySelector('[name="form-telefon"]')?.id,
			voucher: formContainer.querySelector('[name="form-cod-voucher"]')?.id,
			gdprCheckbox: formContainer.querySelector('[name="form-acord-date"]')?.id,
		};

		this.buttons = {
			submit: formContainer.querySelector(`#${formContainer.dataset.idPrefix}ev-form-submit`),
			loading: formContainer.querySelector(`#${formContainer.dataset.idPrefix}form-loading`),
		};

		this.recaptchaContainer = formContainer.querySelector('.e-recaptcha');
		this.explicitRecaptcha = !!this.recaptchaContainer;

		// dataLayer related
		this.formDescriptor = {
			form_name: formName,
			form_type: formType,
			form_category: formCategory,
			form_url: window.location.href.split('?')[0],
		};

		this.initRecaptcha();
		this.init();
		this.addListeners();
		this.validate();
	}

	initRecaptcha() {
		if (!this.recaptchaContainer) {
			return;
		}

		// Randare explicita recaptcha
		// Pe randare implicita, callback-urile onRecaptchaSuccess si onRecaptchaExpired nu sunt legate
		this.recaptchaWidgetId = this.recaptcha.render(this.recaptchaContainer, {
			sitekey: this.recaptchaContainer.dataset.sitekey,
			callback: this.onRecaptchaSuccess.bind(this),
			'expired-callback': this.onRecaptchaExpired.bind(this),
		});
	}

	onRecaptchaSuccess() {
		this.clearResponse('recaptcha');
	}

	onRecaptchaExpired() {
		this.showResponse('error', 'Validarea reCAPTCHA a expirat. Te rugăm să bifezi din nou că nu ești robot.', 'recaptcha');
	}

	init() {
		// Metode custom de validare
		$.validator.addMethod('alpha', customValidate.alpha);
		$.validator.addMethod('phone', customValidate.phone);

		// Restrictionari inputuri
		this.fieldIds.name && restrictInput(`#${this.fieldIds.name}`, regex.lettersSpacesHyphens);
		this.fieldIds.phone && restrictInput(`#${this.fieldIds.phone}`, regex.digits);

		// Reguli de validare
		this.rules = {
			'form-nume': {
				required: true,
				alpha: true,
				minlength: 3,
				maxlength: 50,
			},
			'form-email': {
				required: true,
				email: true,
			},
			'form-telefon': {
				required: true,
				phone: true,
			},
			'form-cod-voucher': {
				minlength: 3,
				maxlength: 15,
			},
			'form-acord-date': {
				required: true,
			},
		};

		// Mesaje eroare la validare
		this.messages = {
			'form-nume': {
				required: customMessages.required(),
				alpha: customMessages.alpha(),
				minlength: customMessages.minlength(),
				maxlength: customMessages.maxlength(),
			},
			'form-email': {
				required: customMessages.required(),
				email: customMessages.email(),
			},
			'form-telefon': {
				required: customMessages.required(),
				phone: customMessages.phone(),
			},
			'form-cod-voucher': {
				minlength: customMessages.minlength(),
				maxlength: customMessages.maxlength(null, 15),
			},
			'form-acord-date': {
				required: customMessages.gdpr(),
			},
		};
	}

	addListeners() {
		// Uppercase pe campul de voucher
		if (this.fieldIds.voucher) {
			document.getElementById(this.fieldIds.voucher).addEventListener('input', (e) => {
				e.target.value = e.target.value.trim().toUpperCase();
			});
		}
	}

	async handleSubmit(form, event) {
		event?.preventDefault();

		const formData = new FormData(form);
		formData.append('action', 'ev_submit_form');
		formData.append('_wpnonce', formData.get(`${form.id}-nonce`));
		formData.delete(`${form.id}-nonce`);

		const recaptchaPassed = this.explicitRecaptcha ? this.recaptcha.getResponse(this.recaptchaWidgetId) : this.recaptcha.getResponse();

		if (recaptchaPassed) {
			this.clearResponse('recaptcha');
			this.setState('loading');

			const url = new URL(form.dataset.fetchUrl);
			url.searchParams.set('action', 'ev_submit_form');

			try {
				const response = await fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(Object.fromEntries(formData)),
				});

				const { success, data } = await response.json();

				if (success) {
					form.reset();
					this.showResponse('success', data.response, 'api');

					window.dataLayer.push({
						event: 'form_submitted',
						form_details: `Nume: ${formData.get('form-nume')}`,
						lead: 'Succes',
						phone_number: `+4${formData.get('form-telefon')}`,
						email: formData.get('form-email'),
						...this.formDescriptor,
					});
				} else {
					this.showResponse('error', data.response, 'api');

					window.dataLayer.push({
						event: 'form_error',
						error_field: data.field,
						error_message: data.response,
						...this.formDescriptor,
					});
				}
			} catch (err) {
				console.error('Fetch error:', err.message);
				this.showResponse('error', 'Am întâmpinat o eroare la trimiterea formularului. Te rugăm să încerci din nou mai târziu.', 'api');
			} finally {
				this.setState();
				if (this.explicitRecaptcha) {
					this.recaptcha.reset(this.recaptchaWidgetId);
				} else {
					this.recaptcha.reset();
				}
			}
		} else {
			this.showResponse('error', 'Bifează că nu ești robot.', 'recaptcha');

			window.dataLayer.push({
				event: 'form_error',
				error_field: 'reCaptcha',
				error_message: 'reCaptcha nebifat',
				form_details: `Nume: ${formData.get('form-nume')}`,
				...this.formDescriptor,
			});
		}
	}

	handleErrors(validator) {
		if (validator.numberOfInvalids() > 0) {
			this.showResponse('error', 'Câmpurile marcate cu roșu conțin erori. Te rugăm să verifici și să reîncerci.', 'validation');
		} else {
			this.clearResponse('validation');
		}
		validator.defaultShowErrors();
	}

	validate() {
		const self = this;

		$(this.form).validate({
			focusInvalid: false,
			submitHandler: async function (form, event) {
				await self.handleSubmit(form, event);
			},
			invalidHandler: function (event, validator) {
				handleFormError(event, validator, self.formDescriptor);
			},
			showErrors: function () {
				self.handleErrors(this);
			},
			rules: this.rules,
			messages: this.messages,
			errorPlacement,
			highlight,
			unhighlight,
			errorClass: 'text-danger line-height-1-3 t-centi e-form-error',
		});
	}

	setState(state = '') {
		const loading = state === 'loading';

		this.buttons.submit && (this.buttons.submit.hidden = loading);
		this.buttons.loading && (this.buttons.loading.hidden = !loading);
	}

	showResponse(type = 'success', msg = '', source = 'api') {
		const container = this.responseContainers[type];
		const msgContainer = this.responseContainers[`${type}Msg`];

		if (!(container && msgContainer)) {
			return;
		}

		// Ascundere celelalte containere
		Object.entries(this.responseContainers).forEach(([key, el]) => {
			if (el && !key.endsWith('Msg') && el !== container) {
				el.hidden = true;
			}
		});

		// Afisare container si plasare mesaj
		msgContainer.innerHTML = msg;
		container.hidden = false;

		// Snapshot raspuns curent
		this.activeResponse = { type, source };
	}

	hideAllResponses() {
		Object.entries(this.responseContainers).forEach(([key, el]) => {
			if (el && !key.endsWith('Msg')) {
				el.hidden = true;
			}
		});
		this.activeResponse = null;
	}

	clearResponse(source) {
		if (this.activeResponse?.source === source) {
			this.hideAllResponses();
		}
	}
}

waitForRecaptcha()
	.then((recaptcha) => {
		recaptcha.ready(() => {
			document.querySelectorAll('.ev-form').forEach((formContainer) => {
				new FormEv({
					formContainer,
					formName: 'Mobilitate verde',
					formType: 'lead',
					formCategory: 'Green',
					recaptcha,
				});
			});
		});
	})
	.catch((err) => {
		console.error(err.message);
		showRecaptchaError('.ev-form');
	});
